import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FileText, Archive, Image as ImageIcon, Film, Download } from 'lucide-react';
import { api, pickLocale } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

const iconFor = (k) => k === 'zip' ? Archive : k === 'image' ? ImageIcon : k === 'video' ? Film : FileText;

export default function MediaCenter() {
  const { t, i18n } = useTranslation();
  useSEO({ title: 'Media Center', description: 'Media assets, brand resources and press downloads for Universal Krishna Gaukul Mission.' });
  const lang = i18n.language.split('-')[0];
  const { data: items = [] } = useQuery({ queryKey: ['media'], queryFn: async () => (await api.get('/media')).data });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="media-center-page">
      <h1 className="font-serif text-4xl md:text-6xl text-ukgm-blue font-semibold leading-tight">{t('media.title')}</h1>
      <p className="mt-4 max-w-2xl text-ukgm-slate text-lg">{t('media.subtitle')}</p>

      {items.length > 0 && (
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {items.map((m, i) => {
            const Icon = iconFor(m.kind);
            return (
              <div key={m.id || i} className="flex items-start gap-4 bg-white border border-ukgm-gold/25 rounded-2xl p-5 card-lift" data-testid={`media-item-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-ukgm-blue text-ukgm-gold flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-ukgm-blue font-semibold">{pickLocale(m.title, lang)}</div>
                  <div className="text-xs text-ukgm-slate mt-1">{pickLocale(m.description, lang)}</div>
                  <div className="mt-1 text-xs text-ukgm-slate">{m.kind?.toUpperCase()} · {m.size}</div>
                </div>
                <a href={m.file_url || '#'} className="shrink-0 inline-flex items-center gap-1.5 bg-ukgm-gold text-ukgm-blue font-bold px-4 py-2 rounded-full text-xs hover:bg-ukgm-goldDark" data-testid={`media-download-${i}`}>
                  <Download className="w-3.5 h-3.5" /> {t('media.download')}
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
